// src/oura/newscan/fixtures/lint.ts
//
// The fixture lint: the checks the backend's lint_queries runs that a draft can
// trip, with its message shapes ("q<n>: ..."). Each issue also carries a code,
// the row key and, where it applies, the word or the other question, so the
// decisions are built from structure and never from parsing the messages.
//
// A brand draft (v4.5) runs the brand checks instead of the local ones, as
// aeo_queries_lint.py --mode brand does: counts 28 / 14 / 8 (or 36 / 14 / 0),
// a market code in every fill, no city, building or "near me", dead shapes
// only in source rows, no category word in a problem row, no brand name or
// claim phrase anywhere; warnings for a row no Google search supports, a home
// market that does not hold the fewest rows and a selling point over 5 words.

import type { ScanQuestionCheck } from "@service/aeo/scanDraftTypes";
import { BRAND_SHAPE, DIFFERENTIATOR_MAX_WORDS, MARKET_CODES, containsPhrase, marketLabel, placeIn, wordCount, type PlaceHit } from "./brand";
import { CATEGORY_CAP, DEAD_SHAPES, NEGATIVE_AFTER_GOOD_FOR } from "./recipe";
import { TOWN_CHOICES } from "../towns";

export type LintCode =
    | "unfilled"
    | "business_name"
    | "dead_shape"
    | "no_place"
    | "statement"
    | "duplicate"
    | "reword_length"
    | "opposite"
    | "long"
    | "category_cap"
    // brand mode
    | "category_word"
    | "claim"
    | "place"
    | "untraced"
    | "home_not_fewest"
    | "differentiator_long"
    | "shape";

export interface LintIssue {
    n: number;
    key: string;
    code: LintCode;
    severity: "error" | "warning";
    message: string;
    /** unfilled: the blank word. */
    variable?: string;
    /** duplicate: the earlier question it repeats. */
    other?: number;
    /** opposite: what the question asks to be "good for". category_word / claim: the word or phrase found. */
    phrase?: string;
    /** place: the city, building or "near me" found. */
    place?: PlaceHit;
}

export interface LintRow {
    n: number;
    key: string;
    check: ScanQuestionCheck;
    text: string;
    reworded: boolean;
    /** Place bindings the row was built with (its outlet, building, station, landmark). */
    ctx: Record<string, string>;
    /** Brand rows: recipe group, fill (the market code) and trace (null = no Google search behind it). */
    group?: string;
    fill?: string;
    trace?: { suggestion: string; market: string } | null;
}

/** What the brand checks need. Absent: a local draft. */
export interface BrandLintContext {
    categoryWords: string[];
    /** Brand names and claim phrases. */
    claimPhrases: string[];
    /** The business's own areas and buildings, banned on top of the city list. */
    places: string[];
    /** Market codes in priority order, home last. */
    markets: string[];
    /** The selling point, when a row uses it. */
    differentiator: string | null;
    /** False: shoppers only, 0 supplier rows. */
    sellsToBusinesses: boolean;
}

export interface LintContext {
    nameTokens: string[];
    /** The outlets (or the market) a location question may name. */
    placeNames: string[];
    /** The profession word, for the cap. Empty skips it. */
    category: string;
    /** Brand mode (v4.5): the brand checks run instead of the local ones. */
    brand?: BrandLintContext | null;
}

export const REWORD_MIN = 8;
export const REWORD_MAX = 220;
const LONG_ABOVE = 110;
const NEAR_DUPLICATE = 0.85;

/**
 * Question openers and stop words. Two questions that differ only in these
 * ("Where can I get X in Y?" vs "Where to get X in Y?") ask the same thing.
 * Backend handoff: the BE lint still runs 0.85 on raw tokens and needs this
 * same list for parity.
 */
const STOP_WORDS = new Set([
    "where", "can", "i", "to", "get", "find", "the", "a", "an", "in", "near", "me", "for",
    "is", "are", "what", "which", "who", "best", "good", "top", "around", "my", "place",
]);

/**
 * Towns of more than one word ("Subang Jaya", "Klang Valley"), longest first.
 * Each counts as one word, so a two-word town weighs what "KL" weighs: without
 * it "Where to get X in Subang Jaya?" and "Where can I find affordable X in
 * Subang Jaya?" read as repeats only because the town is longer.
 */
const LONG_TOWNS = TOWN_CHOICES.filter((t) => t.includes(" "))
    .map((t) => t.toLowerCase())
    .sort((a, b) => b.length - a.length);

/** The words that carry a question's meaning, lower case, punctuation dropped, a long town as one word. */
export function contentTokens(text: string): Set<string> {
    let low = text.toLowerCase().replace(/[?？,.!]/g, "");
    LONG_TOWNS.forEach((t) => {
        if (low.includes(t)) low = low.split(t).join(t.replace(/\s+/g, "_"));
    });
    return new Set(low.split(/\s+/).filter((t) => t && !STOP_WORDS.has(t)));
}

function jaccard(a: Set<string>, b: Set<string>): number {
    let inter = 0;
    a.forEach((x) => {
        if (b.has(x)) inter += 1;
    });
    const union = a.size + b.size - inter;
    return union ? inter / union : 0;
}

function sameSet(a: Set<string>, b: Set<string>): boolean {
    if (a.size !== b.size) return false;
    let same = true;
    a.forEach((x) => {
        if (!b.has(x)) same = false;
    });
    return same;
}

function nearTokens(a: Set<string>, b: Set<string>): boolean {
    if (!a.size || !b.size) return false;
    return sameSet(a, b) || jaccard(a, b) >= NEAR_DUPLICATE;
}

const isExact = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * How close two questions are on the lint's own measure: 1 for an exact copy,
 * else the Jaccard overlap of their content words. The dedupe pass ranks its
 * candidates on this (dedupe.ts).
 */
export function similarity(a: string, b: string): number {
    return isExact(a, b) ? 1 : jaccard(contentTokens(a), contentTokens(b));
}

/** Near-duplicates: same content words, or 0.85 overlap on them. Exact copies count too. */
export function tooSimilar(a: string, b: string): boolean {
    return isExact(a, b) || nearTokens(contentTokens(a), contentTokens(b));
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** The business-name word a text contains, if any. */
function namedToken(text: string, nameTokens: string[]): string | null {
    return nameTokens.find((t) => t && new RegExp(`\\b${escapeRe(t)}\\b`, "i").test(text)) ?? null;
}

/** What a "good for ..." question asks for, when it reads as the opposite. */
function oppositePhrase(text: string): string | null {
    const m = /good for (.+?)[?？]?$/.exec(text.toLowerCase());
    return m && NEGATIVE_AFTER_GOOD_FOR.test(m[1]) ? m[1] : null;
}

function rowIssues(row: LintRow, ctx: LintContext): LintIssue[] {
    const out: LintIssue[] = [];
    const add = (code: LintCode, severity: LintIssue["severity"], message: string, extra: Partial<LintIssue> = {}) =>
        out.push({ n: row.n, key: row.key, code, severity, message: `q${row.n}: ${message}`, ...extra });
    const low = row.text.toLowerCase();

    const blank = /\{([a-z_0-9]+)\}/.exec(row.text);
    // Every blank of the row, not only its first: a second blank (a price after a service) is a word
    // staff must give too, and it has to be asked up front, not after the first one is filled.
    const blanks = Array.from(new Set(Array.from(row.text.matchAll(/\{([a-z_0-9]+)\}/g), (m) => m[1])));
    blanks.forEach((v) => add("unfilled", "error", `unfilled variable(s) {${v}}`, { variable: v }));
    if (row.reworded && (row.text.length < REWORD_MIN || row.text.length > REWORD_MAX))
        add("reword_length", "error", `must be ${REWORD_MIN} to ${REWORD_MAX} characters`);
    if (ctx.brand) {
        brandRowIssues(row, ctx.brand, add);
    } else {
        const token = namedToken(row.text, ctx.nameTokens);
        if (token) add("business_name", "error", `contains the business name (${token})`);
        if (DEAD_SHAPES.some((re) => re.test(low))) add("dead_shape", "error", "a dead question shape");
    }
    if (row.check === "location" && !blank) {
        const own = Object.values(row.ctx).filter(Boolean);
        const names = [...ctx.placeNames, ...own].filter(Boolean);
        if (!names.some((p) => low.includes(p.toLowerCase())))
            add("no_place", "error", `a location question that names none of ${ctx.placeNames.join(", ") || "the outlets"}`);
    }
    if (/\.\s*$/.test(row.text)) add("statement", "error", "reads as a statement");
    const phrase = oppositePhrase(row.text);
    if (phrase) add("opposite", "warning", `asks for the opposite ("good for ${phrase}")`, { phrase });
    if (row.text.length > LONG_ABOVE) add("long", "warning", "long, people type shorter");
    return out;
}

type AddIssue = (code: LintCode, severity: LintIssue["severity"], message: string, extra?: Partial<LintIssue>) => void;

/** The brand-mode row checks (aeo_queries_lint.py --mode brand). */
function brandRowIssues(row: LintRow, b: BrandLintContext, add: AddIssue): void {
    const low = row.text.toLowerCase();
    const claim = b.claimPhrases.find((p) => containsPhrase(row.text, p));
    if (claim) add("claim", "error", `contains the brand's own words (${claim})`, { phrase: claim });
    if (row.group === "pick_problem") {
        const word = b.categoryWords.find((w) => containsPhrase(row.text, w));
        if (word) add("category_word", "error", `a problem question names the product (${word})`, { phrase: word });
    }
    if (row.fill !== undefined && !(MARKET_CODES as ReadonlyArray<string>).includes(row.fill))
        add("place", "error", `asks for ${row.fill}, which is not a market`, { place: { kind: "city", word: row.fill } });
    else {
        const place = placeIn(row.text, b.places);
        if (place) add("place", "error", `names a place (${place.word}); these questions name a market, never a city`, { place });
    }
    if (row.check !== "source" && DEAD_SHAPES.some((re) => re.test(low))) add("dead_shape", "error", "a question shape that never names a business, outside the questions that cite your site");
    if (row.trace === null) add("untraced", "warning", "no Google search behind it");
}

/** The brand-mode set checks: the shape, the home market, the selling point. */
function brandSetIssues(rows: LintRow[], b: BrandLintContext): LintIssue[] {
    const out: LintIssue[] = [];
    const set = (code: LintCode, severity: LintIssue["severity"], message: string, extra: Partial<LintIssue> = {}) =>
        out.push({ n: 0, key: "", code, severity, message, ...extra });
    const count = (pred: (r: LintRow) => boolean) => rows.filter(pred).length;
    const supplier = b.sellsToBusinesses ? BRAND_SHAPE.supplier : 0;
    // Plain names: these messages reach the screen.
    const want: Array<[string, number, number]> = [
        ["Recommend you", BRAND_SHAPE.pick + (BRAND_SHAPE.supplier - supplier), count((r) => r.check === "pick")],
        ["Cite your site", BRAND_SHAPE.source, count((r) => r.check === "source")],
        ["Business buyers", supplier, count((r) => r.check === "supplier")],
        ["Recommend you, products", BRAND_SHAPE.pick_product, count((r) => r.group === "pick_product")],
    ];
    want.forEach(([name, n, got]) => {
        if (got !== n) set("shape", "error", `${name}: ${got} questions, the recipe needs ${n}`);
    });
    if (b.markets.length > 1) {
        const home = b.markets[b.markets.length - 1];
        const per = (m: string) => count((r) => r.fill === m);
        const fewer = b.markets.slice(0, -1).filter((m) => per(m) < per(home));
        if (fewer.length) set("home_not_fewest", "warning", `the home market ${marketLabel(home)} has ${per(home)} questions, more than ${fewer.map(marketLabel).join(", ")}`);
    }
    if (b.differentiator && wordCount(b.differentiator) > DIFFERENTIATOR_MAX_WORDS)
        set("differentiator_long", "warning", `the selling point is ${wordCount(b.differentiator)} words; keep it to ${DIFFERENTIATOR_MAX_WORDS}`, {
            variable: "differentiator",
        });
    return out;
}

/** Every issue in a set, row by row, in row order. */
export function lintRows(rows: LintRow[], ctx: LintContext): LintIssue[] {
    const issues: LintIssue[] = [];
    const toks = rows.map((r) => contentTokens(r.text));
    rows.forEach((row, i) => {
        issues.push(...rowIssues(row, ctx));
        // An exact copy anywhere above wins over a near one, so it still blocks.
        const prior = rows.slice(0, i);
        let j = prior.findIndex((p) => isExact(row.text, p.text));
        const exact = j >= 0;
        if (!exact) j = prior.findIndex((_, k) => nearTokens(toks[i], toks[k]));
        if (j >= 0) {
            // Exact copies block; near-duplicates are the user's call.
            issues.push({
                n: row.n,
                key: row.key,
                code: "duplicate",
                severity: exact ? "error" : "warning",
                message: exact ? `q${row.n}: exact duplicate of q${rows[j].n}` : `q${rows[j].n} and q${row.n} are near-duplicates`,
                other: rows[j].n,
            });
        }
    });
    if (ctx.brand) {
        issues.push(...brandSetIssues(rows, ctx.brand));
        return issues;
    }
    if (ctx.category) {
        const used = rows.filter((r) => r.text.toLowerCase().includes(ctx.category.toLowerCase())).length;
        if (used > CATEGORY_CAP.warnAbove) {
            issues.push({
                n: 0,
                key: "",
                code: "category_cap",
                severity: used > CATEGORY_CAP.failAbove ? "error" : "warning",
                message: `the profession noun is the subject of ${used} questions`,
            });
        }
    }
    return issues;
}

/**
 * Identity of an error across two lint runs, for the "a write may not add an
 * error" rule. Keyed by code and row, as the backend keys by code and slots
 * (handoff 2, new-error rule): filling one blank of a row that has two leaves
 * the same "unfilled" error on that row, so it is not new.
 */
export function errorId(issue: LintIssue): string {
    return issue.code === "unfilled" ? `${issue.code}|${issue.key}` : `${issue.code}|${issue.key}|${issue.variable ?? ""}`;
}
